import { FunctionComponent, useEffect, useState } from 'react'
import { useMutation } from '@apollo/client'
import Dialog from '@/components/_custom/Dialog/Dialog'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import styles from './addTasksNotesDialog.module.scss'
import { useTranslation } from 'react-i18next'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { Child, ChildDaycare } from '@/types/Child'
import { UPDATE_TASK_NOTE } from '@/modules/API/taskNote/taskNoteHandler.ts'
import { TaskNote, TaskNoteStatus, TaskNoteType, TaskNoteUserValue } from '@/types/TaskNote.ts'
import moment, { Moment } from 'moment/moment'
import UserInput from '@/components/_custom/UserInput/userInput.tsx'
import SelectUnique from '@/components/_custom/Selector/selectUnique/selectUnique.tsx'
import { SelectOptionDeprecated, SelectOptionsDeprecated } from '@/types/Select.ts'
import TextArea from '@/components/_custom/TextArea/textArea.tsx'
import Checkbox from '@/components/_custom/Checkbox/Checkbox.tsx'
import SelectMultiple from '@/components/_custom/Selector/selectMultiple/selectMultiple.tsx'
import Loading from '@/components/Loading/Loading.tsx'
import DatePickerComponent from '@/components/_custom/Picker/datePicker/datePicker.tsx'
import TimePickerComponent from '@/components/_custom/Picker/timePicker/timePicker.tsx'
import { Parent } from '@/types/Parent.ts'
import { StaffWithLinks } from '@/types/Staff.ts'
import {
    defineDateLabel,
    defineHourLabel,
    defineTaskNoteStatusOptions,
    getChildComplement,
    isAppointment
} from './addTasksNotesDialog.tsx'
import { Call, EditNote, ListAlt, People, VideoCameraFront } from '@mui/icons-material'
import { useNotification } from '@/modules/context/NotificationContext.tsx'

interface EditTasksNotesDialogProps {
    open: boolean
    handleClose: () => void
    daycareId: number
    parents?: Parent[]
    childs?: Child[]
    staffs?: StaffWithLinks[]
    handleRefetch: () => void
    isRpeOrLaep: boolean
    taskNote: TaskNote
}

const EditTasksNotesDialog: FunctionComponent<EditTasksNotesDialogProps> = ({
    open = false,
    handleClose,
    daycareId,
    parents,
    childs,
    staffs,
    handleRefetch,
    isRpeOrLaep,
    taskNote
}: EditTasksNotesDialogProps) => {
    const { t } = useTranslation()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [taskNoteTitle, setTaskNoteTitle] = useState<string>(taskNote.title)
    const [taskNoteDescription, setTaskNoteDescription] = useState<string>(taskNote.description)
    const [taskNoteType, setTaskNoteType] = useState<string>(taskNote.type)
    const [taskNoteInternalNote, setTaskNoteInternalNote] = useState<boolean>(taskNote.internalNote)

    const [taskStatus, setTaskStatus] = useState<string>(taskNote.status || '')
    const [taskDate, setTaskDate] = useState<string>(taskNote.date || moment().format('DD-MM-YYYY'))

    const now: Moment = moment()
    const rounded: Moment = moment(Math.round(+now / (15 * 60 * 1000)) * (15 * 60 * 1000))

    const [taskStartHour, setTaskStartHour] = useState<string>(
        moment(taskNote.startHour, 'HH:mm:ss').format('HH:mm') || rounded.format('HH:mm')
    )
    const [taskEndHour, setTaskEndHour] = useState<string>(
        moment(taskNote.endHour, 'HH:mm:ss').format('HH:mm') || rounded.add(1, 'h').format('HH:mm')
    )

    const [taskNoteParentIds, setTaskNoteParentIds] = useState<string[]>(
        taskNote.userIds
            .filter((user: TaskNoteUserValue): boolean => user.isParent === true)
            .map((user: TaskNoteUserValue): string => user.id.toString())
    )
    const [taskNoteChildIds, setTaskNoteChildIds] = useState<string[]>(
        taskNote.childIds.map((user: TaskNoteUserValue): string => user.id.toString())
    )

    const [taskNoteStaffIds, setTaskNoteStaffIds] = useState<string[]>(
        taskNote.userIds
            .filter((user: TaskNoteUserValue): boolean => user.isParent === false)
            .map((user: TaskNoteUserValue): string => user.id.toString())
    )

    const [editTaskNote, { loading: loadingUpdate }] = useMutation(UPDATE_TASK_NOTE, {
        variables: {
            daycareId: daycareId,
            taskNoteId: taskNote.id,
            taskNote: {
                title: taskNoteTitle,
                description: taskNoteDescription,
                type: taskNoteType,
                internalNote: taskNoteInternalNote,
                parentIds: taskNoteParentIds.map((id: string): number => Number(id)),
                childIds: taskNoteChildIds.map((id: string): number => Number(id)),
                staffIds: taskNoteStaffIds.map((id: string): number => Number(id)),
                status: taskNoteType !== TaskNoteType.note ? taskStatus : undefined,
                date: taskNoteType !== TaskNoteType.note ? taskDate : undefined,
                startHour: taskNoteType !== TaskNoteType.note ? taskStartHour : undefined,
                endHour: isAppointment(taskNoteType) ? taskEndHour : undefined
            }
        },
        onCompleted: () => {
            handleRefetch()
            handleClose()

            showNotificationSuccess(t('taskNoteEdited'))
        },
        onError: () => {
            showNotificationError(t('taskNoteEditedError'))
        }
    })

    useEffect(() => {
        setTaskNoteTitle(taskNote.title)
        setTaskNoteDescription(taskNote.description)
        handleSelectTaskNoteType(taskNote.type)
        setTaskNoteInternalNote(taskNote.internalNote)

        setTaskStatus(taskNote.status || '')
        setTaskDate(taskNote.date || moment().format('DD-MM-YYYY'))
        setTaskStartHour(moment(taskNote.startHour, 'HH:mm:ss').format('HH:mm') || rounded.format('HH:mm'))
        setTaskEndHour(moment(taskNote.endHour, 'HH:mm:ss').format('HH:mm') || rounded.add(1, 'h').format('HH:mm'))

        setTaskNoteChildIds(taskNote.childIds.map((user: TaskNoteUserValue): string => user.id.toString()))
        setTaskNoteParentIds(
            taskNote.userIds
                .filter((user: TaskNoteUserValue): boolean => user.isParent === true)
                .map((user: TaskNoteUserValue): string => user.id.toString())
        )
        setTaskNoteStaffIds(
            taskNote.userIds
                .filter((user: TaskNoteUserValue): boolean => user.isParent === false)
                .map((user: TaskNoteUserValue): string => user.id.toString())
        )
    }, [taskNote])

    const taskNoteTypeOptions: SelectOptionsDeprecated = [
        {
            options: [
                {
                    name: t('task'),
                    value: 'task',
                    startAdornment: <ListAlt className={styles.IconSelect} />
                },
                {
                    name: t('note'),
                    value: 'note',
                    startAdornment: <EditNote className={styles.IconSelect} />
                },
                {
                    name: t('call'),
                    value: 'call',
                    startAdornment: <Call className={styles.IconSelect} />
                },
                {
                    name: t('appointmentInPerson'),
                    value: 'appointmentInPerson',
                    startAdornment: <People className={styles.IconSelect} />
                },
                {
                    name: t('appointmentVideoconference'),
                    value: 'appointmentVideoconference',
                    startAdornment: <VideoCameraFront className={styles.IconSelect} />
                }
            ]
        }
    ]

    const taskNoteStatusOptions: SelectOptionsDeprecated = [
        {
            options: defineTaskNoteStatusOptions(taskNoteType, t)
        }
    ]

    const parentsOptions: SelectOptionsDeprecated = [
        {
            options: parents
                ? parents
                      .sort((a: Parent, b: Parent): number => {
                          return a.lastname.localeCompare(b.lastname)
                      })
                      .map(
                          (parent: Parent): SelectOptionDeprecated => ({
                              name:
                                  (parent.firstname + ' ' + parent.lastname).trim() === ''
                                      ? parent.email
                                      : parent.firstname + ' ' + parent.lastname,
                              value: parent.id.toString()
                          })
                      )
                : taskNote.userIds
                      .filter((user: TaskNoteUserValue): boolean => user.isParent === true)
                      .map(
                          (user: TaskNoteUserValue): SelectOptionDeprecated => ({
                              name: user.name,
                              value: user.id.toString()
                          })
                      )
        }
    ]

    const childsOptions: SelectOptionsDeprecated = [
        {
            options: childs
                ? childs
                      .filter((child: Child): boolean => {
                          return child.daycares.some(
                              (daycare: ChildDaycare): boolean => daycare.daycareId === daycareId
                          )
                      })
                      .sort((a: Child, b: Child): number => {
                          return a.firstname.localeCompare(b.firstname)
                      })
                      .map(
                          (child: Child): SelectOptionDeprecated => ({
                              name: child.firstname + '' + getChildComplement(child, daycareId),
                              value: child.id.toString()
                          })
                      )
                : taskNote.childIds.map(
                      (user: TaskNoteUserValue): SelectOptionDeprecated => ({
                          name: user.name,
                          value: user.id.toString()
                      })
                  )
        }
    ]

    const staffsOptions: SelectOptionsDeprecated = [
        {
            options: staffs
                ? staffs
                      .sort((a: StaffWithLinks, b: StaffWithLinks): number => {
                          return a.lastname.localeCompare(b.lastname)
                      })
                      .map(
                          (staff: StaffWithLinks): SelectOptionDeprecated => ({
                              name: staff.firstname + ' ' + staff.lastname,
                              value: staff.id.toString()
                          })
                      )
                : taskNote.userIds
                      .filter((user: TaskNoteUserValue): boolean => user.isParent === false)
                      .map(
                          (user: TaskNoteUserValue): SelectOptionDeprecated => ({
                              name: user.name,
                              value: user.id.toString()
                          })
                      )
        }
    ]

    function handleSelectTaskNoteType(value: string): void {
        setTaskNoteType(value)
        switch (value) {
            case TaskNoteType.appointmentInPerson:
            case TaskNoteType.appointmentVideoconference:
                setTaskStatus(TaskNoteStatus.schedule)
                break
            case TaskNoteType.task:
                setTaskStatus(TaskNoteStatus.todo)
                break
            case TaskNoteType.call:
                setTaskStatus(TaskNoteStatus.connected)
                break
        }
    }

    const hasChanged: boolean =
        taskNoteTitle !== taskNote.title ||
        taskNoteDescription !== taskNote.description ||
        taskNoteType !== taskNote.type ||
        taskNoteInternalNote !== taskNote.internalNote ||
        taskStatus !== (taskNote.status || '') ||
        taskDate !== (taskNote.date || moment().format('DD-MM-YYYY')) ||
        taskStartHour !== moment(taskNote.startHour, 'HH:mm:ss').format('HH:mm') ||
        taskEndHour !== moment(taskNote.endHour, 'HH:mm:ss').format('HH:mm') ||
        JSON.stringify(taskNoteParentIds) !==
            JSON.stringify(
                taskNote.userIds
                    .filter((u: TaskNoteUserValue): boolean => u.isParent === true)
                    .map((u: TaskNoteUserValue): string => u.id.toString())
            ) ||
        JSON.stringify(taskNoteChildIds) !==
            JSON.stringify(taskNote.childIds.map((u: TaskNoteUserValue): string => u.id.toString())) ||
        JSON.stringify(taskNoteStaffIds) !==
            JSON.stringify(
                taskNote.userIds
                    .filter((u: TaskNoteUserValue): boolean => u.isParent === false)
                    .map((u: TaskNoteUserValue): string => u.id.toString())
            )

    const disabled: boolean =
        !hasChanged ||
        taskNoteTitle === '' ||
        (taskNoteType !== TaskNoteType.note && (taskDate === '' || taskStartHour === '' || taskEndHour === '')) ||
        (taskNoteStaffIds.length === 0 && taskNoteChildIds.length === 0 && taskNoteParentIds.length === 0) ||
        loadingUpdate

    return (
        <Dialog open={open} handleClose={handleClose}>
            <DialogHeader title={t(defineDialogTitle(taskNoteType))} />
            {loadingUpdate && <Loading overall={false} />}
            <DialogBody className={styles.DialogBody}>
                <div className={styles.InputContainer}>
                    <div className={styles.Input}>
                        <SelectUnique
                            selectOptions={taskNoteTypeOptions}
                            selectValue={taskNoteType}
                            setSelectValue={handleSelectTaskNoteType}
                            formClassName={styles.UserInput}
                            fullWidth
                            label={t('type')}
                        />
                    </div>
                    <div className={styles.BreakLine}></div>
                    <div className={styles.Input}>
                        <UserInput
                            label={t('title')}
                            type={'text'}
                            value={taskNoteTitle}
                            setValue={setTaskNoteTitle}
                            className={styles.UserInput}
                        />
                    </div>
                    <div className={styles.TextAreaInput}>
                        <p className={styles.TextAreaTitle}>{t('description')}</p>
                        <TextArea
                            placeholder={t('description')}
                            className={styles.TextArea}
                            value={taskNoteDescription}
                            onChange={setTaskNoteDescription}
                            maxCount={2000}
                        />
                    </div>
                    {taskNoteType !== TaskNoteType.note && (
                        <>
                            <div className={styles.Input}>
                                <SelectUnique
                                    label={t('status')}
                                    selectOptions={taskNoteStatusOptions}
                                    selectValue={taskStatus}
                                    setSelectValue={setTaskStatus}
                                    formClassName={styles.UserInput}
                                    fullWidth
                                />
                            </div>
                            <div className={styles.Input}>
                                <DatePickerComponent
                                    value={taskDate}
                                    setValue={setTaskDate}
                                    label={t(defineDateLabel(taskNoteType))}
                                    className={styles.UserInput}
                                    disablePast={false}
                                />
                                {!isAppointment(taskNoteType) && (
                                    <TimePickerComponent
                                        value={taskStartHour}
                                        setValue={setTaskStartHour}
                                        label={t(defineHourLabel(taskNoteType))}
                                        className={styles.UserInput}
                                        minuteToDisable={15}
                                    />
                                )}
                            </div>
                            {isAppointment(taskNoteType) && (
                                <div className={styles.Input}>
                                    <TimePickerComponent
                                        value={taskStartHour}
                                        setValue={setTaskStartHour}
                                        label={t(defineHourLabel(taskNoteType))}
                                        className={styles.UserInput}
                                        minuteToDisable={15}
                                    />
                                    <TimePickerComponent
                                        value={taskEndHour}
                                        setValue={setTaskEndHour}
                                        label={t('endHour')}
                                        className={styles.UserInput}
                                        minuteToDisable={15}
                                    />
                                </div>
                            )}
                        </>
                    )}
                    <div className={styles.Input}>
                        <div className={styles.CheckBoxContainer}>
                            <Checkbox
                                onChange={setTaskNoteInternalNote}
                                checked={taskNoteInternalNote}
                                text={
                                    (parents || childs) && isRpeOrLaep
                                        ? t('visibleByPublic')
                                        : parents || childs
                                          ? t('visibleByParent')
                                          : t('visibleByStaff')
                                }
                            />
                        </div>
                    </div>
                    <div className={styles.BreakLine}></div>
                    {(parentsOptions[0].options.length >= 1 || parents) && (
                        <div className={styles.Input}>
                            <SelectMultiple
                                selectOptions={parentsOptions}
                                selectValues={taskNoteParentIds}
                                setSelectValues={setTaskNoteParentIds}
                                className={styles.UserInput}
                                label={isRpeOrLaep ? t('publics') : t('parents')}
                            />
                        </div>
                    )}
                    {(childsOptions[0].options.length >= 1 || childs) && (
                        <div className={styles.Input}>
                            <SelectMultiple
                                selectOptions={childsOptions}
                                selectValues={taskNoteChildIds}
                                setSelectValues={setTaskNoteChildIds}
                                className={styles.UserInput}
                                label={t('childs')}
                            />
                        </div>
                    )}
                    {(staffsOptions[0].options.length >= 1 || staffs) && (
                        <div className={styles.Input}>
                            <SelectMultiple
                                selectOptions={staffsOptions}
                                selectValues={taskNoteStaffIds}
                                setSelectValues={setTaskNoteStaffIds}
                                className={styles.UserInput}
                                label={t('staffs')}
                            />
                        </div>
                    )}
                </div>
                <div className={styles.ButtonContainer}>
                    <ButtonTextIcon type={'close'} onClick={handleClose} className={styles.Button} />
                    <ButtonTextIcon
                        type={'edit'}
                        onClick={editTaskNote}
                        className={styles.Button}
                        disabled={disabled}
                    />
                </div>
            </DialogBody>
        </Dialog>
    )
}

export default EditTasksNotesDialog

function defineDialogTitle(type: string): string {
    switch (type) {
        case TaskNoteType.note:
            return 'editNote'
        case TaskNoteType.task:
            return 'editTask'
        case TaskNoteType.call:
            return 'editCall'
        case TaskNoteType.appointmentInPerson:
        case TaskNoteType.appointmentVideoconference:
            return 'editAppointment'
        default:
            return 'editTask'
    }
}
